import React, { useMemo, useState } from 'react';
import { cn } from '../../utils/classnames';
import InvestmentAllocationComparisonDonuts from './investmentPolicy/InvestmentAllocationComparisonDonuts2';
import BacktestChart from '../../components/ui/charts/BacktestChart';

function formatPercentage(value) {
  const number = Number(value) || 0;

  return `${number.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

const TABS = [
  { label: 'NACIONAL', value: 'national' },
  { label: 'INTERNACIONAL', value: 'international' },
];

const ALLOCATION_GRID = '24px minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1.2fr)';

const COMPOSITION_COLORS = {
  fi_pos: '#2F72A1',
  fi_pre_ipca: '#4497C5',
  fi_international: '#55B3E0',
  multiasset: '#6BC5DB',
  equities_br: '#82D8D5',
  equities_international: '#95DEDD',
  fii: '#A9E4E5',
  alternatives: '#CFEFF1',
  // offshore
  off_fixed_income_long_term: '#2F72A1',
  off_fixed_income_short_term: '#55B3E0',
  off_equities: '#6BC5DB',
  off_reits: '#82D8D5',
  off_commodities: '#A9E4E5',
  account_balance_percentage: '#CFEFF1',
  // unclassified
  unclassified: '#E6FAFB',
};

const Card = ({ title, className = '', children }) => (
  <div className={cn('bg-white rounded-custom20 p-5 sm:p-6 flex flex-col gap-4 min-h-0', className)}>
    <h2 className="text-sm sm:text-base text-blueDarker font-semibold">{title}</h2>

    {children}
  </div>
);

const Group = ({ className = '', children }) => (
  <div className={cn('bg-tealTransparent rounded-custom20 p-3 sm:p-4 lg:p-5', className)}>{children}</div>
);

const DEFAULT_COLOR = '#337BA9';

const PRODUCT_OPTIONS = [
  { value: 'carteira_administrada', label: 'Carteira Administrada' },
  { value: 'pgbl', label: 'PGBL BTG' },
  { value: 'vgbl', label: 'VGBL BTG' },
];

const getProductLabel = (value) => {
  return PRODUCT_OPTIONS.find((option) => option.value === value)?.label || value || '-';
};

const AllocationMobileInfo = ({ label, value, color }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <span className="text-xs font-semibold uppercase text-blueSteel">{label}</span>

    <div className="flex items-start gap-3 min-w-0">
      {color && (
        <span
          className="shrink-0 mt-1"
          style={{
            width: 18,
            height: 18,
            borderRadius: 6,
            backgroundColor: color,
          }}
        />
      )}

      <span className="text-sm font-semibold text-blueDarker break-words min-w-0">{value || '-'}</span>
    </div>
  </div>
);

const AllocationRow = ({ allocation }) => {
  const color = allocation.colorHex || DEFAULT_COLOR;
  const productLabel = getProductLabel(allocation.product);

  return (
    <div className="bg-blueLight rounded-xl md:overflow-hidden">
      <div
        className="hidden md:grid items-center gap-3 px-4 py-3 text-sm text-blueSteel min-w-0"
        style={{ gridTemplateColumns: ALLOCATION_GRID }}
      >
        <div
          className="shrink-0"
          style={{
            width: 18,
            height: 18,
            borderRadius: 6,
            backgroundColor: color,
          }}
        />

        <div className="min-w-0 truncate font-semibold text-blueDarker text-base" title={allocation.name}>
          {allocation.name || '-'}
        </div>

        <div className="min-w-0 truncate" title={productLabel}>
          {productLabel}
        </div>

        <div className="min-w-0 truncate" title={allocation.operation}>
          {allocation.operation || '-'}
        </div>

        <div className="min-w-0 truncate" title={allocation.recurrence}>
          {allocation.recurrence || '-'}
        </div>
      </div>

      <div className="md:hidden flex flex-col gap-4 p-4">
        <AllocationMobileInfo label="Nome" value={allocation.name} color={color} />
        <AllocationMobileInfo label="Produto" value={productLabel} />
        <AllocationMobileInfo label="Operação" value={allocation.operation} />
        <AllocationMobileInfo label="Recorrência" value={allocation.recurrence} />
      </div>
    </div>
  );
};

const InvestmentAllocationsList = ({ allocations, loading }) => {
  if (loading) {
    return <div className="bg-blueLight rounded-xl px-5 py-5 text-blueSteelDark">Carregando alocações...</div>;
  }

  if (!allocations.length) {
    return <div className="bg-blueLight rounded-xl px-5 py-5 text-blueSteelDark">Nenhum investimento cadastrado.</div>;
  }

  return (
    <div className="flex flex-col min-h-0 w-full md:overflow-hidden">
      <div
        className="hidden md:grid items-center gap-3 px-4 pb-3 text-sm font-semibold text-tealDark min-w-0"
        style={{ gridTemplateColumns: ALLOCATION_GRID }}
      >
        <div />
        <div className="min-w-0 truncate">Nome</div>
        <div className="min-w-0 truncate">Produto</div>
        <div className="min-w-0 truncate">Operação</div>
        <div className="min-w-0 truncate">Recorrência</div>
      </div>

      <div className="flex flex-col gap-3 md:gap-4 md:max-h-96 md:overflow-y-auto md:pr-1 md:custom-scrollbar">
        {allocations.map((allocation, idx) => (
          <AllocationRow key={allocation.id || idx} allocation={allocation} />
        ))}
      </div>
    </div>
  );
};

const InvestmentPolicy = ({ data }) => {
  console.log('data', data);

  const [activeTab, setActiveTab] = useState('national');
  const [loading, setLoading] = useState(false);

  const activeData = data?.[activeTab];

  const backtest = useMemo(() => {
    return activeData?.backtest || {};
  }, [activeData]);

  const suggestedComposition = useMemo(() => {
    return activeData?.suggestedComposition || {};
  }, [activeData]);

  const riskAnalysis = useMemo(() => {
    return activeData?.riskAnalysis || {};
  }, [activeData]);

  const currentComposition = useMemo(() => {
    return activeData?.currentComposition || {};
  }, [activeData]);

  const policy = useMemo(() => {
    return activeData?.policy || {};
  }, [activeData]);

  const investmentAllocations = useMemo(() => {
    return Array.isArray(activeData?.investmentAllocations) ? activeData.investmentAllocations : [];
  }, [activeData]);

  const suggestedCompositionItems = useMemo(() => {
    return activeData?.suggestedComposition?.items || [];
  }, [activeData]);

  return (
    <div className="flex flex-col gap-5">
      <div key={activeTab} className="flex flex-col gap-5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <Group className="lg:col-span-8">
            <div className="grid grid-cols-1 lg:grid-cols-8 gap-5">
              <div className="lg:col-span-3 flex flex-col gap-5 lg:min-h-80">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {TABS.map((tab) => {
                    const isActive = activeTab === tab.value;

                    return (
                      <button
                        key={tab.value}
                        type="button"
                        onClick={() => setActiveTab(tab.value)}
                        className={cn(
                          'rounded-xl px-4 py-3 transition-all duration-200',
                          'text-xs font-semibold uppercase',
                          isActive
                            ? 'bg-tealMedium text-white shadow-sm'
                            : 'bg-white text-blueSteel hover:bg-tealSoft hover:text-blueDarker'
                        )}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                <Card title="Carteira inicial" className="min-h-48 sm:min-h-56 lg:flex-1">
                  <div className="flex flex-col gap-3 text-base">
                    {suggestedCompositionItems.map((item) => {
                      const color = COMPOSITION_COLORS[item.colorKey || item.key] || '#CFEFF1';

                      return (
                        <div
                          key={item.key}
                          className="flex items-center justify-between bg-blueLight px-5 py-4"
                          style={{ borderRadius: 12 }}
                        >
                          <div className="flex gap-5 items-center min-w-0">
                            <span
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 6,
                                backgroundColor: color,
                                flexShrink: 0,
                              }}
                            />

                            <span className="truncate text-blueDarker">{item.label}</span>
                          </div>

                          <span className="font-semibold ml-3" style={{ color }}>
                            {formatPercentage(item.value)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>

              <Card title="Composição da carteira inicial" className="lg:col-span-5 min-h-64 sm:min-h-72 lg:min-h-80">
                <InvestmentAllocationComparisonDonuts
                  currentComposition={currentComposition}
                  suggestedComposition={suggestedComposition}
                />
              </Card>
            </div>
          </Group>

          <Group className="lg:col-span-4">
            <div className="flex flex-col gap-5 lg:min-h-80">
              <Card title="Estratégia de investimentos" className="min-h-36 lg:flex-1">
                <div className="flex flex-col gap-3 text-base">
                  <div
                    className="bg-blueLight px-5 py-4 text-blueMedium flex flex-col gap-4"
                    style={{ borderRadius: 12 }}
                  >
                    <div className="flex items-center gap-5">
                      <div
                        className="flex flex-1 items-center bg-white"
                        style={{
                          height: 16,
                          borderRadius: 999,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          className="bg-blueHighlight"
                          style={{
                            width: `${policy.maxAllowedVolatility || 0}%`,
                            height: 10,
                            marginLeft: '3px',
                            marginRight: '3px',
                            borderRadius: 999,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>

                      <span className="text-sm text-blueDarker">
                        {formatPercentage(policy.maxAllowedVolatility, 2)}
                      </span>
                    </div>

                    <span className="block text-lg text-blueDarker">Volatilidade máxima permitida</span>
                  </div>

                  <div
                    className="bg-blueLight px-5 py-4 text-blueMedium flex flex-col gap-4"
                    style={{ borderRadius: 12 }}
                  >
                    <div className="flex items-center gap-5">
                      <div
                        className="flex flex-1 items-center bg-white"
                        style={{
                          height: 16,
                          borderRadius: 999,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          className="bg-tealLight"
                          style={{
                            width: `${(policy.currentVolatility || 0) * 100}%`,
                            height: 10,
                            marginLeft: '3px',
                            marginRight: '3px',
                            borderRadius: 999,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>

                      <span className="text-sm text-blueDarker">
                        {formatPercentage(policy.currentVolatility * 100, 2)}
                      </span>
                    </div>

                    <span className="block text-lg text-blueDarker">Volatilidade atual</span>
                  </div>
                </div>
              </Card>

              <Card title="Liquidez da carteira inicial" className="min-h-36 lg:flex-1" />
            </div>
          </Group>
        </div>

        <Group>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <div className="lg:col-span-7 flex flex-col gap-5 min-w-0">
              <Card title="Backtest" className="min-h-64 sm:min-h-72 overflow-hidden">
                <BacktestChart backtest={backtest} title="" />
              </Card>

              <Card title="Onde eu devo investir?" className="min-h-44 sm:min-h-52 md:overflow-hidden">
                <InvestmentAllocationsList allocations={investmentAllocations} loading={loading} />
              </Card>
            </div>

            <Card title="Análise de Risco" className="lg:col-span-5 min-h-64 sm:min-h-72 lg:min-h-full">
              <div className="flex flex-col gap-3 text-base">
                <div
                  className="flex items-center justify-between gap-4 bg-blueLight px-5 py-4 text-tealExtraLight3 font-semibold"
                  style={{ borderRadius: 12 }}
                >
                  <span>Maior retorno mensal no período</span>
                  <span className="shrink-0">{formatPercentage(riskAnalysis.maxMonthlyReturn * 100, 2)}</span>
                </div>

                <div
                  className="flex items-center justify-between gap-4 bg-blueLight px-5 py-4 text-tealExtraLight3 font-semibold"
                  style={{ borderRadius: 12 }}
                >
                  <span>Menor retorno mensal no período</span>
                  <span className="shrink-0">{formatPercentage(riskAnalysis.minMonthlyReturn * 100, 2)}</span>
                </div>

                <div
                  className="flex items-center justify-between gap-4 bg-blueLight px-5 py-4 text-tealExtraLight3 font-semibold"
                  style={{ borderRadius: 12 }}
                >
                  <span>Retorno acumulado no período</span>
                  <span className="shrink-0">{formatPercentage(riskAnalysis.cumulativeReturn * 100, 2)}</span>
                </div>

                <div
                  className="flex items-center justify-between gap-4 bg-blueLight px-5 py-4 text-blueMedium"
                  style={{ borderRadius: 12 }}
                >
                  <span>Drawndown máximo (stress test)</span>
                  <span className="shrink-0">{formatPercentage(riskAnalysis.maxDrawdown * 100, 2)}</span>
                </div>

                <div
                  className="flex items-center justify-between gap-4 bg-blueLight px-5 py-4 text-blueMedium"
                  style={{ borderRadius: 12 }}
                >
                  <span>Volatilidade mensal</span>
                  <span className="shrink-0">{formatPercentage(policy.currentVolatility * 100, 2)}</span>
                </div>
              </div>
            </Card>
          </div>
        </Group>
      </div>
    </div>
  );
};

export default InvestmentPolicy;
